import { SplashScreen } from "@capacitor/splash-screen";

/**
 * Maximum time we allow the native splash to stay visible after the Pixi
 * view has started initialising. Acts as a safety net so that if the studio
 * boot stalls (network error fetching `server.url`, an exception during
 * `createView`, …) the user is not stuck staring at a frozen splash.
 *
 * The fade itself is short, the user only needs to see the WebView in case
 * something blew up — they can then read the error and refresh.
 */
const SPLASH_FALLBACK_TIMEOUT_MS = 10_000;

let hidden = false;
let fallbackTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * On native platforms, dismisses the native splash screen kept visible by
 * `launchAutoHide: false` (cf. `capacitor.config.ts`). On web, the plugin
 * resolves a no-op shim — safe to call unconditionally.
 *
 * Idempotent: subsequent calls are ignored once the splash has been hidden.
 */
export async function hideNativeSplash(): Promise<void> {
  if (hidden) {
    return;
  }

  hidden = true;

  if (fallbackTimer) {
    clearTimeout(fallbackTimer);
    fallbackTimer = null;
  }

  try {
    await SplashScreen.hide({ fadeOutDuration: 250 });
  } catch (err) {
    console.warn("[splash] hide failed", err);
  }
}

/**
 * Arms the safety-net timer the first time it is called. If `hideNativeSplash`
 * has not run by then, the splash is dismissed regardless so the user is
 * never stuck on a frozen launch screen.
 */
export function armSplashFallback(): void {
  if (hidden || fallbackTimer) {
    return;
  }

  fallbackTimer = setTimeout(() => {
    void hideNativeSplash();
  }, SPLASH_FALLBACK_TIMEOUT_MS);
}
